import React, { useEffect, useRef, useState, useCallback } from 'react';
import iconnotif from '../style/icons/notification.svg';
import Notifhp from './homepage/Notifhp';
import { listNotifications, markAllRead } from '../services/notifications';
import '../style/css/notifbell2026.css';

/*
 * NotificationsBell — the top-navbar bell for the business owner's in-app notifications.
 * Shows an unread-count badge; opening the panel loads the full list and marks everything
 * read (clears the badge). Polls the unread count every 60s so new bookings surface while
 * the app is open (in-app only — no push/SMS). All data is server-scoped to the business.
 */
const POLL_MS = 60000;

const NotificationsBell = () => {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef(null);
    const mountedRef = useRef(true);
    const seqRef = useRef(0);       // monotonic request id — only the latest may commit state

    useEffect(() => () => { mountedRef.current = false; }, []);

    // Refresh the list + unread count. Drops stale/out-of-order responses (an older poll must
    // not override a newer fetch or a just-applied markAllRead) and never sets state post-unmount.
    const refresh = useCallback(async () => {
        const mySeq = ++seqRef.current;
        const { items: rows, unread: u } = await listNotifications();
        if (!mountedRef.current || mySeq !== seqRef.current) return u; // superseded → discard
        setItems(rows);
        setUnread(u);
        return u;
    }, []);

    // Initial load + lightweight poll for the badge while mounted.
    useEffect(() => {
        refresh();
        const t = setInterval(refresh, POLL_MS);
        return () => clearInterval(t);
    }, [refresh]);

    // Close on outside click / Escape.
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
    }, [open]);

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next) {
            setLoading(true);
            const latestUnread = await refresh(); // use the FRESH count, not the stale closure value
            if (!mountedRef.current) return;
            setLoading(false);
            // Opening the panel means the owner has seen them → clear the badge.
            if (latestUnread > 0) {
                const ok = await markAllRead();
                // Invalidate any in-flight refresh so it can't re-raise the badge, then clear.
                if (ok && mountedRef.current) {
                    seqRef.current++;
                    setUnread(0);
                    setItems((prev) => prev.map((n) => ({ ...n, isRead: 1 })));
                }
            }
        }
    };

    return (
        <div className="nb-wrap" ref={wrapRef}>
            <button className="topbar-btn nb-btn" onClick={toggle} aria-label="התראות" aria-expanded={open}>
                <img src={iconnotif} alt="" />
                {unread > 0 && <span className="nb-badge" aria-label={`${unread} התראות שלא נקראו`}>{unread > 99 ? '99+' : unread}</span>}
            </button>

            {open && (
                <div className="nb-panel" role="dialog" aria-label="התראות">
                    <div className="nb-panel-head">
                        <span className="nb-panel-title">התראות</span>
                    </div>
                    <div className="nb-panel-body">
                        {loading ? (
                            <div className="hp-empty">טוען…</div>
                        ) : items.length ? (
                            <div className="hp-notifs">{items.map((n) => <Notifhp key={n.ID} {...n} />)}</div>
                        ) : (
                            <div className="hp-empty">אין התראות חדשות</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsBell;
